import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Bell, Trash2, ArrowRight, Bookmark, Search } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import Seo from "../components/Seo";

const filterLabel = (filters = {}) => {
  const parts = [];
  if (filters.city && filters.city !== "All") parts.push(filters.city);
  if (filters.listing_type === "rental") parts.push("Rentals");
  if (filters.property_type && filters.property_type !== "All") parts.push(filters.property_type);
  if (filters.beds) parts.push(`${filters.beds}+ bd`);
  if (filters.baths) parts.push(`${filters.baths}+ ba`);
  if (filters.min_price) parts.push(`$${Number(filters.min_price).toLocaleString()}+`);
  if (filters.max_price) parts.push(`<$${Number(filters.max_price).toLocaleString()}`);
  if (filters.min_sqft) parts.push(`${Number(filters.min_sqft).toLocaleString()}+ sqft`);
  if (filters.min_garage) parts.push(`${filters.min_garage}+ garage`);
  if (filters.new_construction) parts.push("New only");
  if (filters.subdivision) parts.push(filters.subdivision);
  return parts.length ? parts.join(" · ") : "All Houston Metro";
};

const filterQuery = (filters = {}) => {
  const sp = new URLSearchParams();
  Object.entries(filters).forEach(([k, v]) => {
    if (v === "" || v === null || v === undefined) return;
    if (k === "status" && v === "Active") return; // default
    if (k === "listing_type" && v === "sale") return; // default
    sp.set(k, String(v));
  });
  return sp.toString();
};

export default function SavedSearches() {
  const { user, loading: authLoading, login } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [runCounts, setRunCounts] = useState({});

  const load = async () => {
    try {
      const { data } = await api.get("/saved-searches", { withCredentials: true });
      setItems(data || []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      setLoading(false);
      return;
    }
    load();
  }, [user, authLoading]);

  const runNow = async (id) => {
    try {
      const { data } = await api.post(`/saved-searches/${id}/run`, {}, { withCredentials: true });
      setRunCounts((c) => ({ ...c, [id]: data.count }));
      toast.success(`${data.count} matching home${data.count === 1 ? "" : "s"} found`);
    } catch {
      toast.error("Could not run search");
    }
  };

  const remove = async (id, name) => {
    if (!window.confirm(`Delete "${name}"?`)) return;
    try {
      await api.delete(`/saved-searches/${id}`, { withCredentials: true });
      setItems((prev) => prev.filter((s) => s.id !== id));
      toast("Saved search removed");
    } catch {
      toast.error("Could not delete");
    }
  };

  return (
    <div className="bg-[#F7F4EF] min-h-screen" data-testid="saved-searches-page">
      <Seo
        title="Saved Searches"
        description="Your saved Houston home searches — get a weekly email digest when new matching homes hit the HAR MLS."
        path="/saved-searches"
      />
      <section className="max-w-5xl mx-auto px-6 lg:px-12 py-20">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-8 h-px bg-[#C9A961]" />
          <div className="eyebrow text-[#0A0A0A]">Weekly Digest</div>
        </div>
        <h1 className="font-display text-5xl lg:text-6xl leading-[0.95] tracking-tight font-light">
          Saved <span className="serif-italic text-[#E63946]">searches.</span>
        </h1>
        <p className="text-[#555] mt-5 max-w-xl font-light">
          We&apos;ll email you every Monday morning with new homes that match. Add or remove any search anytime.
        </p>

        {authLoading || loading ? (
          <div className="mt-16 text-sm text-gray-500">Loading…</div>
        ) : !user ? (
          <div className="mt-16 bg-white border border-dashed border-gray-300 rounded-2xl p-14 text-center max-w-2xl">
            <div className="w-14 h-14 rounded-full bg-[#E63946]/10 text-[#E63946] flex items-center justify-center mx-auto mb-5">
              <Bookmark className="w-6 h-6" strokeWidth={1.75} />
            </div>
            <div className="font-display text-3xl">Sign in to save searches</div>
            <p className="text-[#555] mt-3">
              Create a free account and get a weekly digest of new homes matching your criteria — before they hit third-party portals.
            </p>
            <button
              onClick={login}
              className="mt-7 bg-[#E63946] hover:bg-[#c9313d] text-white px-7 py-3.5 rounded-md font-semibold text-sm inline-flex items-center gap-2"
              data-testid="saved-searches-signin"
            >
              Sign in <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        ) : items.length === 0 ? (
          <div className="mt-16 bg-white border border-dashed border-gray-300 rounded-2xl p-14 text-center max-w-2xl">
            <div className="w-14 h-14 rounded-full bg-[#E63946]/10 text-[#E63946] flex items-center justify-center mx-auto mb-5">
              <Search className="w-6 h-6" strokeWidth={1.75} />
            </div>
            <div className="font-display text-3xl">No saved searches yet</div>
            <p className="text-[#555] mt-3">
              Head to Properties, build your dream filter (market area, beds, price, new construction, garage, sqft…), and hit <b>&quot;Save this search&quot;</b>.
            </p>
            <Link
              to="/properties"
              className="mt-7 inline-flex items-center gap-2 bg-[#1A1A1A] hover:bg-[#333] text-white px-7 py-3.5 rounded-md font-semibold text-sm"
              data-testid="saved-searches-browse"
            >
              Browse properties <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        ) : (
          <div className="mt-12 space-y-4">
            {items.map((s) => (
              <div
                key={s.id}
                className="bg-white border border-gray-200 rounded-2xl p-6 flex items-start justify-between gap-6 flex-wrap"
                data-testid={`saved-search-${s.id}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="font-display text-xl text-[#1A1A1A] truncate">{s.name}</div>
                  <div className="text-sm text-gray-500 mt-1">{filterLabel(s.filters)}</div>
                  <div className="flex flex-wrap items-center gap-4 text-[11px] text-gray-400 mt-3">
                    <span className="flex items-center gap-1">
                      <Bell className="w-3 h-3" />
                      {s.notify ? "Weekly digest ON" : "Digest OFF"}
                    </span>
                    <span>Saved {new Date(s.created_at).toLocaleDateString()}</span>
                    {s.last_notified_at && (
                      <span>Last email: {new Date(s.last_notified_at).toLocaleDateString()}</span>
                    )}
                    {runCounts[s.id] !== undefined && (
                      <span className="text-[#E63946] font-semibold">
                        {runCounts[s.id]} matching now
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    onClick={() => runNow(s.id)}
                    className="bg-white border border-gray-200 hover:border-[#E63946] hover:text-[#E63946] px-4 py-2 rounded-md text-xs font-semibold"
                    data-testid={`saved-search-run-${s.id}`}
                  >
                    Run now
                  </button>
                  <Link
                    to={`/properties?${filterQuery(s.filters)}`}
                    className="bg-[#1A1A1A] hover:bg-[#333] text-white px-4 py-2 rounded-md text-xs font-semibold flex items-center gap-1"
                    data-testid={`saved-search-open-${s.id}`}
                  >
                    Open <ArrowRight className="w-3.5 h-3.5" />
                  </Link>
                  <button
                    onClick={() => remove(s.id, s.name)}
                    className="text-gray-400 hover:text-[#E63946] p-2"
                    aria-label="Delete"
                    data-testid={`saved-search-delete-${s.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
